import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Spinner, Alert, Row, Col } from "react-bootstrap";
import axios from "axios";

function EditGameModal({ show, handleClose, game, onGameUpdated, seasonId }) {
  const [formData, setFormData] = useState({
    game_type: "regular",
    scheduled_date: "",
    location: "",
    home_team: "",
    away_team: "",
    official: "",
  });

  const [locations, setLocations] = useState([]);
  const [teams, setTeams] = useState([]);
  const [officials, setOfficials] = useState([]);
  const [gameTypes, setGameTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Initialize form data when game prop changes
  useEffect(() => {
    if (game) {
      const homeParticipant = game.participants?.find((p) => p.is_home && p.content_type === 'team');
      const awayParticipant = game.participants?.find((p) => p.is_away && p.content_type === 'team');
      const officialParticipant = game.participants?.find((p) => p.content_type === 'official');

      // Format the scheduled_date for datetime-local input
      let formattedDate = "";
      if (game.scheduled_date) {
        const date = new Date(game.scheduled_date);
        // Format as YYYY-MM-DDTHH:MM for datetime-local input in local timezone
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        formattedDate = `${year}-${month}-${day}T${hours}:${minutes}`;
      }

      setFormData({
        game_type: game.game_type || "regular",
        scheduled_date: formattedDate,
        location: game.location?.id || "",
        home_team: (game.game_type === 'playoff' && !homeParticipant) ? 'tbd' : (homeParticipant?.object_id || ""),
        away_team: (game.game_type === 'playoff' && !awayParticipant) ? 'tbd' : (awayParticipant?.object_id || ""),
        official: officialParticipant?.object_id || "",
      });
    }
  }, [game]);

  // Fetch dropdown data when the modal opens
  useEffect(() => {
    if (!show) return;

    // Fetch locations
    axios.get("/api/games/locations/", {
      withCredentials: true,
    })
      .then((response) => setLocations(response.data))
      .catch((error) => console.error("Error fetching locations:", error));

    // Fetch teams for the season
    axios.get(`/api/teams/${seasonId}/teams/`, {
      withCredentials: true,
    })
      .then((response) => setTeams(response.data))
      .catch((error) => console.error("Error fetching teams:", error));

    // Fetch officials for the season
    axios.get(`/api/officials/season/${seasonId}/`, {
      withCredentials: true,
    })
      .then((response) => setOfficials(response.data))
      .catch((error) => console.error("Error fetching officials:", error));

    // Fetch game types
    axios.get("/api/games/game_types/", {
      withCredentials: true,
    })
      .then((response) => setGameTypes(response.data))
      .catch((error) => console.error("Error fetching game types:", error));
  }, [show, seasonId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // If game type is changed to bye, clear away team
    if (name === 'game_type' && value === 'bye') {
      setFormData(prev => ({ ...prev, away_team: "" }));
    }
  };

  const validateForm = () => {
    if (!formData.scheduled_date) {
      setErrorMessage("Date and time are required");
      return false;
    }
    const isPlayoff = formData.game_type === 'playoff';
    const bothConcreteTeams = formData.home_team && formData.away_team && formData.home_team !== 'tbd' && formData.away_team !== 'tbd';
    if (formData.game_type !== 'bye' && bothConcreteTeams && String(formData.home_team) === String(formData.away_team)) {
      setErrorMessage("Home and away teams must be different");
      return false;
    }
    if (formData.game_type !== 'bye' && !isPlayoff && !formData.away_team) {
      setErrorMessage("Away team is required for non-bye games");
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage("");

    if (!validateForm()) {
      setLoading(false);
      return;
    }

    try {
      // Format the scheduled_date for the backend
      let formattedScheduledDate = formData.scheduled_date;
      if (formData.scheduled_date) {
        // Keep the datetime-local value as-is (it's already in local timezone)
        // Just add seconds to make it compatible with backend parsing
        formattedScheduledDate = formData.scheduled_date + ':00';
      }

      // Update the game details
      const updateData = {
        game_type: formData.game_type,
        scheduled_date: formattedScheduledDate,
        location_id: formData.location ? parseInt(formData.location) : null,
      };

      // Add team changes if they've been modified
      const homeParticipant = game.participants?.find((p) => p.is_home && p.content_type === 'team');
      const awayParticipant = game.participants?.find((p) => p.is_away && p.content_type === 'team');
      const officialParticipant = game.participants?.find((p) => p.content_type === 'official');

      const currentHomeTeamId = homeParticipant?.object_id;
      const currentAwayTeamId = awayParticipant?.object_id;
      const currentOfficialId = officialParticipant?.object_id;

      const isPlayoff = formData.game_type === 'playoff';

      // Home team handling
      if (isPlayoff) {
        if (formData.home_team === 'tbd' || formData.home_team === "") {
          updateData.home_team_tbd = true;
        } else if (formData.home_team !== currentHomeTeamId) {
          updateData.home_team_id = parseInt(formData.home_team);
        }
      } else {
        if (formData.home_team !== currentHomeTeamId && formData.home_team !== "") {
          updateData.home_team_id = parseInt(formData.home_team);
        }
      }

      // Away team handling
      if (isPlayoff) {
        if (formData.away_team === 'tbd' || formData.away_team === "") {
          updateData.away_team_tbd = true;
        } else if (formData.away_team) {
          updateData.away_team_id = parseInt(formData.away_team);
        }
      } else if (formData.game_type !== 'bye') {
        // For non-bye, non-playoff games always include away_team_id when provided
        if (formData.away_team) {
          updateData.away_team_id = parseInt(formData.away_team);
        }
      }

      // Add official changes if modified
      if (formData.official !== currentOfficialId) {
        updateData.official_id = formData.official ? parseInt(formData.official) : null;
      }


      await axios.patch(`/api/games/${game.id}/`, updateData, {
        withCredentials: true,
      });

      onGameUpdated();
      handleClose();
    } catch (error) {
      console.error("Error updating game:", error);
      setErrorMessage(error.response?.data?.error || "Failed to update game");
    } finally {
      setLoading(false);
    }
  };

  if (!game) return null;

  const isByeGame = formData.game_type === 'bye';
  const isPlayoffGame = formData.game_type === 'playoff';

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Edit Game</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {errorMessage && <Alert variant="danger">{errorMessage}</Alert>}
        <Form onSubmit={handleSubmit}>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="game_type">
                <Form.Label>Game Type</Form.Label>
                <Form.Control
                  as="select"
                  name="game_type"
                  value={formData.game_type}
                  onChange={handleChange}
                  required
                >
                  {gameTypes.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="scheduled_date">
                <Form.Label>Date & Time *</Form.Label>
                <Form.Control
                  type="datetime-local"
                  name="scheduled_date"
                  value={formData.scheduled_date}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group controlId="location">
                <Form.Label>Location</Form.Label>
                <Form.Control
                  as="select"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                >
                  <option value="">Select a location</option>
                  {locations.map((location) => (
                    <option key={location.id} value={location.id}>
                      {location.name}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="home_team">
                <Form.Label>{isByeGame ? "Bye Team" : "Home Team"}</Form.Label>
                <Form.Control
                  as="select"
                  name="home_team"
                  value={formData.home_team}
                  onChange={handleChange}
                >
                  <option value="">Select a {isByeGame ? "bye" : "home"} team</option>
                  {isPlayoffGame && (
                    <option value="tbd">TBD</option>
                  )}
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>
                      {team.name}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
            {/* Away team field - only show for non-bye games */}
            {!isByeGame && (
              <Col md={6}>
                <Form.Group controlId="away_team">
                  <Form.Label>Away Team</Form.Label>
                  <Form.Control
                    as="select"
                    name="away_team"
                    value={formData.away_team}
                    onChange={handleChange}
                  >
                    <option value="">Select an away team</option>
                    {isPlayoffGame && (
                      <option value="tbd">TBD</option>
                    )}
                    {teams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
            )}
          </Row>
          <Row className="mb-3">
            <Col md={6}>
              <Form.Group controlId="official">
                <Form.Label>Official</Form.Label>
                <Form.Control
                  as="select"
                  name="official"
                  value={formData.official}
                  onChange={handleChange}
                >
                  <option value="">Select an official (optional)</option>
                  {officials.map((official) => (
                    <option key={official.id} value={official.id}>
                      {official.user_name}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
          </Row>
          <Alert variant="info" className="mb-3">
            <i className="bi bi-info-circle me-2"></i>
            <strong>Note:</strong> All changes (game type, date, time, location, teams, and official) will be saved immediately.
            Team changes will automatically update the player participants for the game.
            {isByeGame && " Bye games only include the selected team's players."}
          </Alert>
          <div className="text-end">
            <Button variant="secondary" onClick={handleClose} className="me-2">
              Cancel
            </Button>
            <Button variant="primary" type="submit" disabled={loading}>
              {loading ? <Spinner animation="border" size="sm" /> : "Save Changes"}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default EditGameModal; 